import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function AdminPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [planFilter, setPlanFilter] = useState('all');

    useEffect(() => {
        if (!user?.is_staff) {
            navigate('/');
            return;
        }
        api.get('/admin/dashboard/')
            .then((res) => setData(res.data))
            .catch(() => setError('Failed to load admin data'))
            .finally(() => setLoading(false));
    }, [user, navigate]);

    if (loading) {
        return (
            <Layout>
                <div className="page-container">
                    <div className="admin-loading card">
                        <div className="admin-loading-bar" />
                        <div className="admin-loading-bar short" />
                        <p>Loading admin dashboard...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="page-container">
                    <div className="card admin-error-card">
                        <h2>Could not load admin dashboard</h2>
                        <p>{error}</p>
                    </div>
                </div>
            </Layout>
        );
    }

    const totalUsers = data.users.total || 0;
    const verifiedRate = Math.round((data.users.verified / Math.max(totalUsers, 1)) * 100);
    const conversionRate = Math.round((data.subscriptions.paid_total / Math.max(totalUsers, 1)) * 100);
    const planTotal = Math.max(
        data.subscriptions.free +
            data.subscriptions.starter +
            data.subscriptions.pro +
            data.subscriptions.max,
        1
    );

    const donutSegments = [
        { key: 'free', label: 'Free', value: data.subscriptions.free, color: '#94a3b8' },
        { key: 'starter', label: 'Starter', value: data.subscriptions.starter, color: '#2563eb' },
        { key: 'pro', label: 'Pro', value: data.subscriptions.pro, color: '#0f766e' },
        { key: 'max', label: 'Max', value: data.subscriptions.max, color: '#f59e0b' },
    ];

    let offset = 0;
    const donutStops = donutSegments
        .map((segment) => {
            const pct = (segment.value / planTotal) * 100;
            const start = offset;
            const end = offset + pct;
            offset = end;
            return `${segment.color} ${start}% ${end}%`;
        })
        .join(', ');

    const funnelRows = [
        { label: 'Registered', value: totalUsers, color: '#2563eb' },
        { label: 'Verified', value: data.users.verified, color: '#10b981' },
        { label: 'Paid', value: data.subscriptions.paid_total, color: '#f59e0b' },
    ];

    const maxSignups = Math.max(...data.daily_signups.map((d) => d.count), 1);

    const filteredUsers = data.recent_users.filter((u) => {
        const matchesQuery = u.email.toLowerCase().includes(query.toLowerCase());
        const plan = u.subscription_plan || 'free';
        const matchesPlan = planFilter === 'all' || plan === planFilter;
        return matchesQuery && matchesPlan;
    });

    const statCards = [
        {
            label: 'Total Users',
            value: totalUsers,
            note: `${data.users.new_this_week} new this week`,
            accent: 'blue',
        },
        {
            label: 'Verified Users',
            value: data.users.verified,
            note: `${verifiedRate}% verification rate`,
            accent: 'teal',
        },
        {
            label: 'Monthly Revenue',
            value: `$${data.revenue.mrr}`,
            note: 'Estimated recurring revenue',
            accent: 'amber',
        },
        {
            label: 'Paid Conversion',
            value: `${conversionRate}%`,
            note: `${data.subscriptions.paid_total} paid users`,
            accent: 'violet',
        },
    ];

    return (
        <Layout>
            <div className="page-container admin-page">
                <section className="admin-hero">
                    <div>
                        <p className="admin-eyebrow">Internal analytics</p>
                        <h1>Admin Command Center</h1>
                        <p className="subtitle">Live performance, growth, and revenue signals in one place.</p>
                    </div>
                    <div className="admin-hero-badges">
                        <span className="admin-badge live">Live</span>
                        <span className="admin-badge">Last 30 days</span>
                    </div>
                </section>

                <section className="admin-kpi-grid">
                    {statCards.map((stat) => (
                        <article key={stat.label} className={`admin-kpi-card accent-${stat.accent}`}>
                            <p className="admin-kpi-label">{stat.label}</p>
                            <h2>{stat.value}</h2>
                            <p className="admin-kpi-note">{stat.note}</p>
                            <div className="admin-kpi-sparkline" aria-hidden="true">
                                {data.daily_signups.map((d) => (
                                    <span
                                        key={`${stat.label}-${d.date}`}
                                        style={{ height: `${Math.max((d.count / maxSignups) * 22, 3)}px` }}
                                    />
                                ))}
                            </div>
                        </article>
                    ))}
                </section>

                <section className="admin-grid top">
                    <article className="card admin-card signups">
                        <div className="admin-card-head">
                            <h3>Signups Last 7 Days</h3>
                            <span>{data.users.new_this_week} this week</span>
                        </div>

                        <div className="admin-signups-chart">
                            {data.daily_signups.map((day) => {
                                const height = Math.max((day.count / maxSignups) * 100, 6);
                                return (
                                    <div key={day.date} className="admin-signups-col">
                                        <span className="value">{day.count}</span>
                                        <div className="bar-wrap">
                                            <div className="bar" style={{ height: `${height}%` }} />
                                        </div>
                                        <span className="label">{day.date}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </article>

                    <article className="card admin-card plans">
                        <div className="admin-card-head">
                            <h3>Plan Distribution</h3>
                            <span>{data.subscriptions.paid_total} paid users</span>
                        </div>

                        <div className="admin-donut-wrap">
                            <div className="admin-donut" style={{ background: `conic-gradient(${donutStops})` }}>
                                <div className="admin-donut-core">
                                    <strong>{totalUsers}</strong>
                                    <small>users</small>
                                </div>
                            </div>
                            <ul className="admin-donut-legend">
                                {donutSegments.map((segment) => (
                                    <li key={segment.key}>
                                        <span className="dot" style={{ background: segment.color }} />
                                        <span>{segment.label}</span>
                                        <strong>{segment.value}</strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </article>
                </section>

                <section className="admin-grid bottom">
                    <article className="card admin-card revenue">
                        <div className="admin-card-head">
                            <h3>Revenue Snapshot</h3>
                            <span>Estimated from active plans</span>
                        </div>
                        <div className="admin-revenue-grid">
                            <div>
                                <p>MRR</p>
                                <h4>${data.revenue.mrr}</h4>
                            </div>
                            <div>
                                <p>ARR</p>
                                <h4>${data.revenue.arr}</h4>
                            </div>
                        </div>
                    </article>

                    <article className="card admin-card funnel">
                        <div className="admin-card-head">
                            <h3>User Funnel</h3>
                            <span>From signup to paid</span>
                        </div>
                        <div className="admin-funnel-list">
                            {funnelRows.map((row) => (
                                <div key={row.label} className="admin-funnel-row">
                                    <div className="meta">
                                        <p>{row.label}</p>
                                        <strong>{row.value}</strong>
                                    </div>
                                    <div className="track">
                                        <div
                                            className="fill"
                                            style={{
                                                width: `${Math.max((row.value / Math.max(totalUsers, 1)) * 100, 6)}%`,
                                                background: row.color,
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </article>
                </section>

                <section className="card admin-table-card">
                    <div className="admin-card-head table-head">
                        <div>
                            <h3>Recent Users</h3>
                            <span>{filteredUsers.length} records shown</span>
                        </div>
                        <div className="admin-table-tools">
                            <input
                                type="search"
                                placeholder="Search email"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
                                <option value="all">All plans</option>
                                <option value="free">Free</option>
                                <option value="starter">Starter</option>
                                <option value="pro">Pro</option>
                                <option value="max">Max</option>
                            </select>
                        </div>
                    </div>

                    <div className="admin-table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Plan</th>
                                    <th>Credits</th>
                                    <th>Verified</th>
                                    <th>Joined</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((u) => {
                                    const plan = u.subscription_plan || 'free';
                                    return (
                                        <tr key={u.id}>
                                            <td>{u.email}</td>
                                            <td>
                                                <span className={`admin-plan-pill plan-${plan}`}>{plan}</span>
                                            </td>
                                            <td>{u.credits}</td>
                                            <td>
                                                <span
                                                    className={`admin-verify-pill ${
                                                        u.is_email_verified ? 'ok' : 'no'
                                                    }`}
                                                >
                                                    {u.is_email_verified ? 'Verified' : 'Pending'}
                                                </span>
                                            </td>
                                            <td>{new Date(u.created_at).toLocaleDateString()}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </Layout>
    );
}

export default AdminPage;
